import type { ReactNode } from 'react';
import type { GroupIdEnum } from 'app/modules/Segment/types';

export const PROFILE_MGM_MENU_LABEL = 'Indique e ganhe pontos!';
export const PROFILE_EDIT_MENU_LABEL = 'Editar';
export const PROFILE_ABOUT_MENU_LABEL = 'Sobre a Jusfy';

export const MGM_INVITE_ACTION_KEY = 'mgm_invite' as const;

export enum DrawerType {
  PROCESSOS = 'processos',
  CLIENTES = 'clientes',
  CONFIGURACOES = 'configuracoes',
  JUSFYPAY = 'jusfypay',
}

export enum MenuItemId {
  DASHBOARD = 'dashboard',
  PROCESSOS = 'processos',
  CLIENTES = 'clientes',
  JUSFYPAY = 'jusfypay',
  IA_JUSFY = 'ia-jusfy',
  JURISPRUDENCIA = 'jurisprudencia',
  GAMIFICACAO = 'gamificacao',
  AFILIADOS = 'afiliados',
  CONFIGURACOES = 'configuracoes',
  PERFIL = 'perfil',
  MEU_DRIVE = 'meu-drive',
  ESTUDOS = 'estudos',
  CARREIRA = 'carreira',
  CURSOS = 'cursos',
}

export enum DrawerItemId {
  USUARIOS = 'usuarios',
  CONSULTAS_LOTE = 'consultas-lote',
  PLANO_PAGAMENTO = 'plano-pagamento',
  PERFIL = 'perfil',
  SEGURANCA = 'seguranca',
  AGENDA = 'agenda',
  JUSMAIL = 'jusmail',
  CRIADOR_CONTEUDO = 'criador-conteudo',
  JUSCORE_RH = 'juscore-rh',
  ANTECIPACAO_COMO_FUNCIONA = 'antecipacao-como-funciona',
  ANTECIPACAO_MEU_PAINEL = 'antecipacao-meu-painel',
}

export enum MenuItemType {
  MAIN = 'main',
  SETTINGS = 'settings',
  DRAWER = 'drawer',
}

export enum SidebarIconVariant {
  DEFAULT = 'default',
  REWARD_BADGE = 'reward-badge',
}

export enum SidebarBadgeType {
  NEW_TAG = 'newTag',
  NOTIFICATION_TAG = 'notificationTag',
}

export interface MenuItem {
  id: MenuItemId;
  label: string;
  icon: ReactNode;
  route: string;
  isNew?: boolean;
  noveltyDate?: Date;
  drawerType?: DrawerType | null;
  type?: MenuItemType;
  activePrefixes?: string[];
  variant?: SidebarIconVariant;
  tooltip?: string | boolean;
  hideLabel?: boolean;
  compactLabel?: boolean;
  trackEvent?: {
    groupId: GroupIdEnum;
    categoryParent?: string | null;
  };
}

export type SidebarBadgeConfig =
  | { kind: SidebarBadgeType.NEW_TAG; shimmer?: boolean }
  | { kind: SidebarBadgeType.NOTIFICATION_TAG; count: number };

export interface ResolvedMenuItem extends MenuItem {
  show: boolean;
  disabled: boolean;
  disabledTooltip?: string;
  badge?: SidebarBadgeConfig;
}

export interface DrawerItemInput {
  icon: ReactNode;
  label: string;
  path: string;
  parentDrawer: DrawerType;
  id?: DrawerItemId;
  externalLink?: boolean;
  onClick?: () => void;
  toggle?: ReactNode;
  toggleKey?: string;
  activePrefixes?: string[];
  exactMatch?: boolean;
  trackEvent?: {
    groupId: GroupIdEnum;
    categoryParent?: string | null;
  };
  hideBadge?: boolean;
  highlight?: boolean;
  badge?: SidebarBadgeConfig;
  isNew?: boolean;
  noveltyDate?: Date;
}

export interface ResolvedDrawerItem extends DrawerItemInput {
  show: boolean;
  disabled: boolean;
  disabledTooltip?: string;
  badge?: SidebarBadgeConfig;
}

export interface DrawerSection {
  title: string | null;
  items: DrawerItemInput[];
}

export interface ResolvedDrawerSection {
  title: string;
  items: ResolvedDrawerItem[];
}

export interface MenuDrawerConfig {
  processos: DrawerSection[];
  clientes: DrawerSection[];
  configuracoes: DrawerSection[];
  jusfypay: DrawerSection[];
}

export interface ResolvedMenuDrawerConfig {
  processos: ResolvedDrawerSection[];
  clientes: ResolvedDrawerSection[];
  configuracoes: ResolvedDrawerSection[];
  jusfypay: ResolvedDrawerSection[];
}

export type MobileMenuFooterActionKey = typeof MGM_INVITE_ACTION_KEY;

export type MobileMenuFooterItem =
  | {
      type: 'action';
      actionKey: MobileMenuFooterActionKey;
      label: string;
    }
  | {
      type: 'item';
      id: string;
      label: string;
      path: string;
      icon: ReactNode;
      externalLink?: boolean;
      hideIcon?: boolean;
    };

export type FlattenedMenuActionKey = MobileMenuFooterActionKey;

export type PushItemParams = Pick<FlattenedMenuEntryItem, 'icon' | 'label' | 'groupId'> &
  Partial<
    Pick<
      FlattenedMenuEntryItem,
      | 'path'
      | 'actionKey'
      | 'externalLink'
      | 'iconPosition'
      | 'iconSize'
      | 'iconColor'
      | 'badge'
      | 'categoryParent'
      | 'showIcon'
      | 'disabled'
      | 'disabledTooltip'
    >
  >;

export interface FlattenedMenuEntryItem {
  type: 'item';
  id: string;
  icon: ReactNode;
  label: string;
  path?: string;
  actionKey?: FlattenedMenuActionKey;
  externalLink?: boolean;
  iconPosition?: 'left' | 'right';
  iconSize?: number;
  iconColor?: string;
  badge?: SidebarBadgeConfig;
  categoryParent?: string | null;
  groupId: GroupIdEnum;
  showIcon?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}

export interface FlattenedMenuEntrySectionTitle {
  type: 'sectionTitle';
  id: string;
  label: string;
}

export interface FlattenedMenuEntryDivider {
  type: 'divider';
  id: string;
}

export type FlattenedMenuEntry =
  | FlattenedMenuEntryItem
  | FlattenedMenuEntrySectionTitle
  | FlattenedMenuEntryDivider;
